import { z } from "zod";

import { ArrayBinaryOperator, TernaryOperator } from "../../operators.ts";
import type { IResolvableBooleanArrayValue, IResolvableBooleanValue } from "../../values.ts";
import { ResolvableBooleanArrayValue, ResolvableBooleanValue } from "../../values.ts";
import type { IArrayBinaryCondition, IArrayTernaryCondition } from "./common.ts";
import { arrayBinaryCondition, arrayTernaryCondition } from "./common.ts";

// Zod value schemas
const BooleanArrayValue = z.union([z.array(z.boolean()), ResolvableBooleanArrayValue]);
const BooleanValue = z.union([z.boolean(), ResolvableBooleanValue]);

// Zod schemas with type discriminator
export const BooleanArrayBinaryCondition = arrayBinaryCondition(
  z.literal("booleanArray"),
  ArrayBinaryOperator,
  BooleanArrayValue,
  BooleanValue,
);
export type BooleanArrayBinaryCondition = z.infer<typeof BooleanArrayBinaryCondition>;

export const BooleanArrayTernaryCondition = arrayTernaryCondition(
  z.literal("booleanArray"),
  TernaryOperator,
  BooleanArrayValue,
  BooleanValue,
);
export type BooleanArrayTernaryCondition = z.infer<typeof BooleanArrayTernaryCondition>;

export const BooleanArrayCondition = z.union([BooleanArrayBinaryCondition, BooleanArrayTernaryCondition]);
export type BooleanArrayCondition = z.infer<typeof BooleanArrayCondition>;

// Interfaces for complex types (verified to match schemas)
export interface IBooleanArrayBinaryCondition extends IArrayBinaryCondition<
  boolean[] | IResolvableBooleanArrayValue,
  boolean | IResolvableBooleanValue
> {
  type: "booleanArray";
}

export interface IBooleanArrayTernaryCondition extends IArrayTernaryCondition<
  boolean[] | IResolvableBooleanArrayValue,
  boolean | IResolvableBooleanValue
> {
  type: "booleanArray";
}

// Type compatibility assertions - compile-time check that inferred types match interfaces
type _AssertBinaryCompatible = BooleanArrayBinaryCondition extends IBooleanArrayBinaryCondition ? true : never;
type _AssertTernaryCompatible = BooleanArrayTernaryCondition extends IBooleanArrayTernaryCondition ? true : never;
const _binaryCheck: _AssertBinaryCompatible = true;
const _ternaryCheck: _AssertTernaryCompatible = true;
void _binaryCheck;
void _ternaryCheck;
